import { AbstractControl, FormGroup } from "@angular/forms";

export class Utilities {
  /**
   * @info formatInputValue method is used for formatting the input values of the name initials.
   * Example:
   * yourObject = { initials: a b }
   * Expected outcome: A. B.
   * */
  static formatInputValue(formGroup: FormGroup, controlName: string): void {
    const control = formGroup.get(controlName);
    const currentValue = control?.value;
    if (this.stringIsInvalid(currentValue)) {
      control?.setErrors({ required: true });
      return;
    }
    const validCharacters = currentValue.match(/[a-zA-Z]/g);
    if (!validCharacters || validCharacters.length === 0) {
      control?.setErrors({ incorrect: true });
    } else {
      control?.setErrors(null);
      const formattedValue = this.transformInitials(validCharacters);
      control?.setValue(formattedValue);
    }
  }

  static transformInitials(characters: Array<string>): string {
    return characters.join(".").toUpperCase() + ".";
  }

  /**
   * @info formatLastNameValue method is used for capitalizing the first letter of the lastName field.
   * Example:
   * yourObject = { lastName: talero }
   * Expected outcome: Talero
   * */
  static formatLastNameValue(formGroup: FormGroup, controlName: string): void {
    const control = formGroup.get(controlName);
    const currentValue = control?.value;
    if (this.stringIsInvalid(currentValue)) {
      control?.setErrors({ required: true });
      return;
    }
    this.setFormattedValue(control, this.transformFirstLetter, currentValue);
  }

  static stringIsInvalid(value: string): boolean {
    return !value || value.trim().length === 0;
  }

  static setFormattedValue(
    control: AbstractControl<any, any> | null,
    stringTransformer: (value: string) => void,
    value: string
  ): void {
    control?.setErrors(null);
    const formattedValue = stringTransformer(value);
    control?.setValue(formattedValue);
  }

  static transformFirstLetter(value: string): string {
    if (value[0] !== value[0].toUpperCase()) {
      return value[0].toUpperCase() + value.slice(1);
    }

    return value;
  }

  /**
   * @info formatPhoneNumberValue method is used for formatting phone number values.
   * Example:
   * yourObject = { phoneNumber: 0317891231 }
   * Expected outcome: +0317891231
   * */
  static formatPhoneNumberValue(
    formGroup: FormGroup,
    controlName: string
  ): void {
    const control = formGroup.get(controlName);
    const currentValue = control?.value;
    if (this.stringIsInvalid(currentValue)) {
      control?.setErrors({ required: true });
      return;
    }
    this.setFormattedValue(control, this.addPlusToPhoneNumber, currentValue);
  }

  static addPlusToPhoneNumber(value: string): string {
    if (value[0] !== "+") {
      return "+" + value;
    }

    return value;
  }

  static downloadAttachment = (file: Blob, fileName: string): void => {
    const fileURL = URL.createObjectURL(file);
    const fileLink = document.createElement("a");
    fileLink.href = fileURL;
    fileLink.download = `${fileName}`;
    document.body.appendChild(fileLink);
    fileLink.click();
    URL.revokeObjectURL(fileURL);
    fileLink.remove();
  };
}
